import React, { useState } from 'react';
import { 
  Scale, 
  BookOpen, 
  Globe, 
  Building2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Shield
} from 'lucide-react';
import { BRAND } from '../config/branding';

const sources = [
  {
    name: "Légifrance",
    description: "Codes & textes de loi",
    url: "https://www.legifrance.gouv.fr",
    icon: BookOpen,
    official: true
  },
  {
    name: "Judilibre",
    description: "Jurisprudence judiciaire",
    url: "https://www.courdecassation.fr/acces-rapide-judilibre",
    icon: Scale,
    official: true
  },
  {
    name: "Conseil d'État",
    description: "Jurisprudence administrative",
    url: "https://www.conseil-etat.fr",
    icon: Building2,
    official: true
  },
  {
    name: "EUR-Lex",
    description: "Droit européen",
    url: "https://eur-lex.europa.eu",
    icon: Globe,
    official: true
  },
  {
    name: "HUDOC",
    description: "Jurisprudence CEDH",
    url: "https://hudoc.echr.coe.int",
    icon: Shield,
    official: true
  }
];

// Compact version for sidebar
export const OfficialSourcesCompact = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="px-2 py-2 border-t border-[#B69D74]/10">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-2 hover:bg-white/5 transition-colors duration-200"
      >
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#B69D74]" />
          <span className="text-xs text-[#94A3B8] uppercase tracking-wider">
            Sources officielles
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="w-3 h-3 text-[#94A3B8]" />
        ) : (
          <ChevronDown className="w-3 h-3 text-[#94A3B8]" />
        )}
      </button>

      {expanded && (
        <div className="mt-1 space-y-0.5 animate-fade-in">
          {sources.map((source) => (
            <a
              key={source.name}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between px-2 py-1.5 text-xs hover:bg-white/5 transition-colors duration-200 group"
            >
              <div className="flex items-center gap-2">
                <source.icon className="w-3 h-3 text-[#B69D74]" strokeWidth={1.5} />
                <div>
                  <span className="text-[#F5F5EF] group-hover:text-[#B69D74]">{source.name}</span>
                </div>
              </div>
              <ExternalLink className="w-3 h-3 text-[#94A3B8] opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
            </a>
          ))}
          
          <div className="px-2 pt-2 mt-1 border-t border-[#B69D74]/5">
            <p className="text-[10px] text-[#94A3B8]/60 leading-relaxed">
              Données issues des bases officielles de la République française et de l'Union européenne
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Badge version for login/landing pages
export const OfficialSourcesBadge = ({ className = "" }) => {
  return (
    <div className={`${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-1 h-4 bg-[#B69D74]" />
        <span className="text-xs text-[#94A3B8] uppercase tracking-widest">
          Données officielles
        </span>
      </div>
      
      <div className="flex flex-wrap gap-3">
        {sources.slice(0, 4).map((source) => (
          <div 
            key={source.name}
            className="flex items-center gap-1.5 text-xs text-[#94A3B8]"
          >
            <source.icon className="w-3 h-3 text-[#B69D74]" strokeWidth={1.5} />
            <span>{source.name}</span>
          </div>
        ))}
      </div>
      
      <p className="text-[10px] text-[#94A3B8]/50 mt-3">
        Connecté aux bases juridiques officielles françaises et européennes
      </p>
    </div>
  );
};

// Full card version for settings or about page
export const OfficialSourcesCard = () => {
  return (
    <div className="diplomat-card p-4">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-[#B69D74]/10">
          <Scale className="w-5 h-5 text-[#B69D74]" strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="text-sm font-heading font-semibold text-[#F5F5EF]">
            Sources Juridiques Officielles
          </h3>
          <p className="text-xs text-[#94A3B8]">
            Données issues des bases publiques françaises et européennes
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {sources.map((source) => (
          <a
            key={source.name}
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-2 p-3 bg-[#1F2839] border border-[#B69D74]/10 hover:border-[#B69D74]/30 transition-colors duration-200 group"
          >
            <source.icon className="w-4 h-4 text-[#B69D74] mt-0.5" strokeWidth={1.5} />
            <div>
              <p className="text-xs font-medium text-[#F5F5EF] group-hover:text-[#B69D74]">
                {source.name}
              </p>
              <p className="text-[10px] text-[#94A3B8]">
                {source.description}
              </p>
            </div>
          </a>
        ))}
      </div>

      <div className="mt-4 pt-3 border-t border-[#B69D74]/10">
        <p className="text-[10px] text-[#94A3B8]/60 leading-relaxed">
          {BRAND.name} utilise les API publiques officielles mises à disposition par la Direction de l'Information
          Légale et Administrative (DILA), le Conseil d'État, la Cour de cassation, et les institutions européennes. 
          Les données sont consultées en temps réel pour garantir leur actualité.
        </p>
      </div>
    </div>
  );
};

export default OfficialSourcesCompact;
